import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, X } from 'lucide-react';

// leg_ — GDPR cookie consent banner. Shown on first visit until the user
// accepts/rejects. Persists choice in localStorage (no cookies needed for
// the consent flag itself, ironic as that may be).

const STORAGE_KEY = 'cookie_consent_v1';

type Category = 'necessary' | 'analytics' | 'marketing';

interface ConsentState {
  accepted: boolean;
  categories: Category[];
  timestamp: string;
}

function load(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentState;
  } catch {
    return null;
  }
}

function save(state: ConsentState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage disabled — we just render again next load.
  }
}

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!load()) setVisible(true);
  }, []);

  function persist(categories: Category[]) {
    save({
      accepted: categories.length > 0,
      categories,
      timestamp: new Date().toISOString(),
    });
    setVisible(false);
    setCustomizing(false);
  }

  const acceptAll = () => persist(['necessary', 'analytics', 'marketing']);
  const acceptNecessary = () => persist(['necessary']);
  const acceptCustom = () => {
    const chosen: Category[] = ['necessary'];
    if (analytics) chosen.push('analytics');
    if (marketing) chosen.push('marketing');
    persist(chosen);
  };

  if (!visible) return null;

  return (
    <div
      className="cookie-consent-banner no-print fixed inset-x-0 bottom-0 z-[60] border-t border-slate-200 bg-white/95 backdrop-blur-sm shadow-2xl dark:bg-slate-900/95 dark:border-slate-700"
      role="dialog"
      aria-label={t('leg_.cookies.title', 'Zgoda na pliki cookies')}
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-start">
        <div className="shrink-0 rounded-full bg-brand-500/10 p-2 text-brand-500">
          <Cookie size={20} />
        </div>
        <div className="flex-1 text-sm text-slate-700 dark:text-slate-300">
          {!customizing ? (
            <>
              <p className="font-semibold text-slate-900 dark:text-white">
                {t('leg_.cookies.title', 'Używamy plików cookies')}
              </p>
              <p className="mt-1 text-xs">
                {t(
                  'leg_.cookies.desc',
                  'Używamy cookies niezbędnych do działania serwisu oraz opcjonalnie analitycznych i marketingowych. Możesz zmienić wybór w każdej chwili.',
                )}{' '}
                <a href="/privacy" className="text-brand-600 underline hover:text-brand-700">
                  {t('leg_.cookies.learn_more', 'Dowiedz się więcej')}
                </a>
              </p>
            </>
          ) : (
            <div className="space-y-3">
              <p className="font-semibold text-slate-900 dark:text-white">
                {t('leg_.cookies.customize_title', 'Dostosuj preferencje cookies')}
              </p>
              <div className="space-y-2">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="mt-1 h-4 w-4 rounded border-slate-300"
                  />
                  <span>
                    <span className="block font-medium">
                      {t('leg_.cookies.cat_necessary', 'Niezbędne')}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {t(
                        'leg_.cookies.cat_necessary_desc',
                        'Sesja, CSRF, preferencje logowania. Zawsze włączone.',
                      )}
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300"
                  />
                  <span>
                    <span className="block font-medium">
                      {t('leg_.cookies.cat_analytics', 'Analityczne')}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {t(
                        'leg_.cookies.cat_analytics_desc',
                        'Anonimowe pomiary ruchu i wydajności UX.',
                      )}
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300"
                  />
                  <span>
                    <span className="block font-medium">
                      {t('leg_.cookies.cat_marketing', 'Marketingowe')}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {t(
                        'leg_.cookies.cat_marketing_desc',
                        'Spersonalizowane komunikaty (obecnie nieużywane).',
                      )}
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:items-end">
          {!customizing ? (
            <>
              <button onClick={acceptAll} className="btn-primary text-xs">
                {t('leg_.cookies.accept_all', 'Akceptuj wszystkie')}
              </button>
              <button onClick={acceptNecessary} className="btn-secondary text-xs">
                {t('leg_.cookies.accept_necessary', 'Tylko niezbędne')}
              </button>
              <button
                onClick={() => setCustomizing(true)}
                className="btn-ghost text-xs"
              >
                {t('leg_.cookies.customize', 'Dostosuj')}
              </button>
            </>
          ) : (
            <>
              <button onClick={acceptCustom} className="btn-primary text-xs">
                {t('leg_.cookies.save_prefs', 'Zapisz preferencje')}
              </button>
              <button
                onClick={() => setCustomizing(false)}
                className="btn-ghost text-xs"
              >
                <X size={14} /> {t('leg_.cookies.back', 'Wróć')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
